/**
 * O som do terreno: mergulho, chiado, atolo e ignição.
 *
 * O terreno tinha imagem e não tinha voz. Entrar num lago, apagar o fogo na
 * água, atolar no charco e pegar fogo na lava eram quatro acontecimentos
 * importantes — e nenhum deles fazia barulho, o que numa tela de celular com o
 * tanque pequeno significa que metade das vezes o jogador não percebia que
 * tinha acontecido.
 *
 * Tudo procedural, pelo mesmo motivo do resto do `proceduralSfx`: são quatro
 * sons curtos que não valem quatro arquivos para baixar, e assim eles existem
 * mesmo antes de qualquer asset ser publicado.
 *
 * Cada som é construído a partir do que a coisa é no mundo:
 *  - **mergulho**: ruído filtrado que abre e fecha rápido — a massa de água
 *    engolindo o casco;
 *  - **chiado**: ruído agudo com decaimento longo — vapor escapando, o som de
 *    "resolveu-se";
 *  - **atolo**: ruído grave e curto com um estalo, sem brilho nenhum — lama não
 *    respinga, ela suga;
 *  - **ignição**: sopro grave crescente — o contrário do chiado, e por isso o
 *    par sonoro que ensina a regra (acendeu × apagou) sem uma palavra.
 */

import { getSharedAudioContext } from "./proceduralSfx";

/**
 * Ruído branco de `seconds` segundos.
 *
 * Gerado por chamada e não em cache: são buffers de menos de meio segundo, o
 * custo é irrelevante perto de um `AudioBufferSourceNode`, e um buffer
 * compartilhado faria todos os respingos soarem exatamente iguais.
 */
function noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/** Mergulho na água: ruído que abre e fecha, com um "glub" grave por baixo. */
export function playSplashSound(volume = 0.4): void {
  const ctx = getSharedAudioContext();
  if (!ctx || volume <= 0) return;
  const now = ctx.currentTime;

  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, 0.45);

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 0.9;
  // A varredura de agudo para grave é o que faz o ouvido ler "algo entrou na
  // água" em vez de "chiado": o respingo começa brilhante e afunda.
  filter.frequency.setValueAtTime(2400, now);
  filter.frequency.exponentialRampToValueAtTime(420, now + 0.35);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(now);
  source.stop(now + 0.45);

  // O corpo grave: a massa de água deslocada.
  const glub = ctx.createOscillator();
  const glubGain = ctx.createGain();
  glub.type = "sine";
  glub.frequency.setValueAtTime(240, now);
  glub.frequency.exponentialRampToValueAtTime(90, now + 0.18);
  glubGain.gain.setValueAtTime(0.001, now);
  glubGain.gain.exponentialRampToValueAtTime(volume * 0.5, now + 0.02);
  glubGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
  glub.connect(glubGain);
  glubGain.connect(ctx.destination);
  glub.start(now);
  glub.stop(now + 0.24);
}

/** Vapor: o fogo encontrou a água. Longo e agudo, o oposto da ignição. */
export function playSteamSound(volume = 0.45): void {
  const ctx = getSharedAudioContext();
  if (!ctx || volume <= 0) return;
  const now = ctx.currentTime;

  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, 0.8);

  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.setValueAtTime(1800, now);
  filter.frequency.exponentialRampToValueAtTime(4200, now + 0.6);

  const gain = ctx.createGain();
  // Ataque quase instantâneo e cauda longa: o chiado do vapor não tem "batida",
  // ele começa forte e vai embora escapando.
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(now);
  source.stop(now + 0.8);
}

/** Atolo: grave, curto e sem brilho. Lama não respinga, ela suga. */
export function playMudSound(volume = 0.4): void {
  const ctx = getSharedAudioContext();
  if (!ctx || volume <= 0) return;
  const now = ctx.currentTime;

  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, 0.3);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(700, now);
  filter.frequency.exponentialRampToValueAtTime(200, now + 0.25);
  filter.Q.value = 2;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(now);
  source.stop(now + 0.3);

  // O estalo do casco descolando do barro.
  const pop = ctx.createOscillator();
  const popGain = ctx.createGain();
  pop.type = "triangle";
  pop.frequency.setValueAtTime(150, now + 0.05);
  pop.frequency.exponentialRampToValueAtTime(70, now + 0.16);
  popGain.gain.setValueAtTime(0.001, now + 0.05);
  popGain.gain.exponentialRampToValueAtTime(volume * 0.45, now + 0.07);
  popGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  pop.connect(popGain);
  popGain.connect(ctx.destination);
  pop.start(now + 0.05);
  pop.stop(now + 0.2);
}

/** Ignição: o sopro que pega. Grave crescente — o negativo do chiado. */
export function playIgniteSound(volume = 0.5): void {
  const ctx = getSharedAudioContext();
  if (!ctx || volume <= 0) return;
  const now = ctx.currentTime;

  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx, 0.6);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  // Ao contrário do vapor, o filtro **abre**: o fogo cresce depois de pegar.
  filter.frequency.setValueAtTime(300, now);
  filter.frequency.exponentialRampToValueAtTime(1600, now + 0.3);
  filter.Q.value = 1.2;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.09);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(now);
  source.stop(now + 0.6);

  // Estouro grave curto: o instante em que pegou.
  const woof = ctx.createOscillator();
  const woofGain = ctx.createGain();
  woof.type = "sawtooth";
  woof.frequency.setValueAtTime(90, now);
  woof.frequency.exponentialRampToValueAtTime(45, now + 0.25);
  woofGain.gain.setValueAtTime(0.001, now);
  woofGain.gain.exponentialRampToValueAtTime(volume * 0.35, now + 0.05);
  woofGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  woof.connect(woofGain);
  woofGain.connect(ctx.destination);
  woof.start(now);
  woof.stop(now + 0.32);
}
